import { useCallback, useEffect, useRef, useState } from "react";

import { useCssVarInitializer } from "./useCssVarInitializer";

export type ButtonPositionAnchorSide = "left" | "right";
export type ButtonPositionAnchorVertical = "top" | "bottom";

export type ButtonPosition = {
    /**
     * Vertical offset in px.
     * Interpreted as distance from `anchorVertical` (top/bottom).
     */
    y: number;
    /**
     * Horizontal offset in px.
     * Interpreted as distance from `anchorSide` (left/right).
     */
    x: number;
};

export type UseButtonPositionOptions = {
    anchorSide: ButtonPositionAnchorSide;
    anchorVertical: ButtonPositionAnchorVertical;
    className?: string;
    cssVarX: string;
    cssVarY: string;
    maxCssInitAttempts?: number;
};

export function useButtonPosition({
    anchorSide: anchorSideProp,
    anchorVertical: anchorVerticalProp,
    className,
    cssVarX,
    cssVarY,
    maxCssInitAttempts = 10,
}: UseButtonPositionOptions) {
    const [position, setPosition] = useState<ButtonPosition>({ y: 0, x: 0 });
    const [isDraggingState, setIsDraggingState] = useState(false);
    // Internal anchor side — can flip automatically when the user drops the button
    // on the opposite half of the viewport.
    const [anchorSide, setAnchorSide] = useState<ButtonPositionAnchorSide>(anchorSideProp);
    // Keep a ref so drag-closure callbacks always see the latest value without
    // needing to be recreated.
    const anchorSideRef = useRef<ButtonPositionAnchorSide>(anchorSide);
    anchorSideRef.current = anchorSide;

    // Internal anchor vertical — flips between top/bottom on drop.
    const [anchorVertical, setAnchorVertical] = useState<ButtonPositionAnchorVertical>(anchorVerticalProp);
    const anchorVerticalRef = useRef<ButtonPositionAnchorVertical>(anchorVertical);
    anchorVerticalRef.current = anchorVertical;

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const hasInitializedFromCss = useRef(false);
    const hasUserMoved = useRef(false);

    const isDragging = useRef(false);
    const origin = useRef({ mouseX: 0, mouseY: 0, x: 0, y: 0 });

    const activeMouseMoveHandler = useRef<((ev: MouseEvent) => void) | null>(null);
    const activeMouseUpHandler = useRef<((ev: MouseEvent) => void) | null>(null);

    const removeDragListeners = useCallback(() => {
        if (activeMouseMoveHandler.current) {
            document.removeEventListener("mousemove", activeMouseMoveHandler.current);
            activeMouseMoveHandler.current = null;
        }

        if (activeMouseUpHandler.current) {
            document.removeEventListener("mouseup", activeMouseUpHandler.current);
            activeMouseUpHandler.current = null;
        }

        isDragging.current = false;
    }, []);

    useCssVarInitializer({
        elementRef: wrapperRef,
        cssVarX,
        cssVarY,
        deps: [className],
        shouldSkip: () => hasInitializedFromCss.current || hasUserMoved.current,
        maxAttempts: maxCssInitAttempts,
        onResolved: ({ x, y }) => {
            hasInitializedFromCss.current = true;
            setPosition((prev) => ({
                x: typeof x === "number" ? Math.max(0, x) : prev.x,
                y: typeof y === "number" ? Math.max(0, y) : prev.y,
            }));
        },
    });

    const getMaxOffsets = useCallback(() => {
        const el = wrapperRef.current;
        const elWidth = el ? el.offsetWidth : 0;
        const elHeight = el ? el.offsetHeight : 0;
        return {
            maxX: Math.max(0, window.innerWidth - elWidth),
            maxY: Math.max(0, window.innerHeight - elHeight),
        };
    }, []);

    useEffect(() => {
        const handleWindowResize = () => {
            setPosition((prev) => {
                const { maxX, maxY } = getMaxOffsets();

                const nextX = Math.min(prev.x, maxX);
                const nextY = Math.min(prev.y, maxY);

                if (nextX === prev.x && nextY === prev.y) {
                    return prev;
                }

                return {
                    ...prev,
                    x: nextX,
                    y: nextY,
                };
            });
        };

        window.addEventListener("resize", handleWindowResize);

        return () => {
            removeDragListeners();
            window.removeEventListener("resize", handleWindowResize);
        };
    }, [removeDragListeners, getMaxOffsets]);

    const handleDragMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();

            // Defensive: if a previous drag didn't finish cleanly, remove stale handlers.
            removeDragListeners();

            isDragging.current = true;
            setIsDraggingState(true);

            origin.current = {
                mouseX: e.clientX,
                mouseY: e.clientY,
                x: position.x,
                y: position.y,
            };

            // Capture both anchor values at drag-start so the move math stays
            // consistent for the entire drag gesture, even if the refs flip.
            const dragAnchorSide = anchorSideRef.current;
            const dragAnchorVertical = anchorVerticalRef.current;

            const onMouseMove = (ev: MouseEvent) => {
                if (!isDragging.current) {
                    return;
                }

                hasUserMoved.current = true;

                const deltaX =
                    dragAnchorSide === "left" ? ev.clientX - origin.current.mouseX : origin.current.mouseX - ev.clientX;

                const deltaY =
                    dragAnchorVertical === "top"
                        ? ev.clientY - origin.current.mouseY
                        : origin.current.mouseY - ev.clientY;

                const { maxX, maxY } = getMaxOffsets();

                setPosition({
                    x: Math.min(Math.max(0, origin.current.x + deltaX), maxX),
                    y: Math.min(Math.max(0, origin.current.y + deltaY), maxY),
                });
            };

            const onMouseUp = (ev: MouseEvent) => {
                isDragging.current = false;
                setIsDraggingState(false);

                const el = wrapperRef.current;
                const elWidth = el ? el.offsetWidth : 0;
                const elHeight = el ? el.offsetHeight : 0;

                const viewportMidX = window.innerWidth / 2;
                const viewportMidY = window.innerHeight / 2;

                const newAnchorSide: ButtonPositionAnchorSide = ev.clientX > viewportMidX ? "right" : "left";
                const newAnchorVertical: ButtonPositionAnchorVertical = ev.clientY > viewportMidY ? "bottom" : "top";

                setPosition((prev) => {
                    const { maxX, maxY } = getMaxOffsets();

                    // Re-express x from the old horizontal anchor edge to the new one.
                    const elementLeftEdge = dragAnchorSide === "right" ? window.innerWidth - prev.x - elWidth : prev.x;
                    const newX =
                        newAnchorSide === "right"
                            ? Math.max(0, window.innerWidth - elementLeftEdge - elWidth)
                            : Math.max(0, elementLeftEdge);

                    // Re-express y from the old vertical anchor edge to the new one.
                    const elementTopEdge =
                        dragAnchorVertical === "bottom" ? window.innerHeight - prev.y - elHeight : prev.y;
                    const newY =
                        newAnchorVertical === "bottom"
                            ? Math.max(0, window.innerHeight - elementTopEdge - elHeight)
                            : Math.max(0, elementTopEdge);

                    return {
                        x: Math.min(newX, maxX),
                        y: Math.min(newY, maxY),
                    };
                });

                if (newAnchorSide !== dragAnchorSide) {
                    setAnchorSide(newAnchorSide);
                }
                if (newAnchorVertical !== dragAnchorVertical) {
                    setAnchorVertical(newAnchorVertical);
                }

                removeDragListeners();
            };

            activeMouseMoveHandler.current = onMouseMove;
            activeMouseUpHandler.current = onMouseUp;

            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
        },
        [anchorSide, anchorVertical, position.x, position.y, removeDragListeners, getMaxOffsets]
    );

    return {
        position,
        anchorSide,
        anchorVertical,
        isDraggingState,
        wrapperRef,
        handleDragMouseDown,
    };
}
